<template>
  <BottomSheet
    ref="sheet"
    elevated
    :title="label"
    data-cy="select-options-sheet"
    @show-sheet="focusFilter"
    @hide-sheet="emit('close', kept)"
  >
    <template v-if="multiselect" #action>
      <SeraButton
        class="ml-auto"
        size="small"
        label="Done"
        @click="close(true)"
      />
    </template>
    <template #header>
      <SelectInputFilter
        ref="filter"
        v-model="query"
        class="mt-3"
        :placeholder="filterPlaceholder"
      />
    </template>
    <slot></slot>
  </BottomSheet>
</template>

<script setup lang="ts">
import { nextTick, ref } from "vue";

import BottomSheet from "@/components/Seranova/BottomSheet/BottomSheet.vue";
import SeraButton from "@/components/Seranova/Buttons/Button.vue";
import SelectInputFilter from "./SelectInputFilter.vue";

// The sheet presentation of a select's rows: the label and, for multi-select,
// a Done button in the header above the search field, the slotted rows
// scrolling below. Moves focus into the search field once the sheet is up and
// reports `close` after the leave transition has played, saying whether the
// selection is kept (Done, or a close the select asked for) or was dismissed
// (close icon, backdrop, Escape).
withDefaults(
  defineProps<{
    label?: string;
    filterPlaceholder?: string;
    multiselect?: boolean;
  }>(),
  { label: "", filterPlaceholder: "Filter options...", multiselect: false }
);

const emit = defineEmits<{
  (e: "close", kept: boolean): void;
}>();

const query = defineModel<string>("query", { default: "" });

const sheet = ref<InstanceType<typeof BottomSheet> | null>(null);
const filter = ref<InstanceType<typeof SelectInputFilter> | null>(null);

const focusFilter = () => {
  nextTick(() => filter.value?.focus());
};

let kept = false;

const close = (keep = true) => {
  kept = keep;
  sheet.value?.close();
};

defineExpose({ close });
</script>
